import React from "react";
import { Web3ReactProvider } from "@web3-react/core";
import { Heading, ThemeProvider, Paragraph } from "theme-ui";

import { BatchedWebSocketAugmentedWeb3Provider } from "@liquity/providers";
import { LiquityProvider } from "./hooks/LiquityContext";
import { WalletConnector } from "./components/WalletConnector";
import { TransactionProvider } from "./components/Transaction";
import { Icon } from "./components/Icon";
import { getConfig } from "./config";
import theme from "./theme";
import { DisposableWalletProvider } from "./testUtils/DisposableWalletProvider";
import { LiquityFrontend } from "./LiquityFrontend";
import { AppLoader } from "./components/AppLoader";
import ReactGA from "react-ga4";
import { capitalize } from "lodash";

const MESUREMENT_ID = process.env.REACT_APP_GA_ID; // measurement ID
if (process.env.NODE_ENV === "production" && !!MESUREMENT_ID) {
  ReactGA.initialize(MESUREMENT_ID);
}

if (window.ethereum) {
  // Silence MetaMask warning in console
  Object.assign(window.ethereum, { autoRefreshOnNetworkChange: false });
}

if (process.env.REACT_APP_DEMO_MODE === "true") {
  const ethereum = new DisposableWalletProvider(
    process.env.REACT_APP_RPC_URL || `http://${window.location.hostname}:8545`,
    "0x4d5db4107d237df6a3d58ee5f70ae63d73d7658d4026f2eefd2f204c81682cb7"
  );

  Object.assign(window, { ethereum });
}

// Start pre-fetching the config
getConfig().then(config => {
  // console.log("Frontend config:");
  // console.log(config);
  Object.assign(window, { config });
});

const EthersWeb3ReactProvider: React.FC = ({ children }) => {
  return (
    <Web3ReactProvider getLibrary={provider => new BatchedWebSocketAugmentedWeb3Provider(provider)}>
      {children}
    </Web3ReactProvider>
  );
};

const UnsupportedMainnetFallback: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-screen text-center bg-main text-style-plain">
    <Heading sx={{ mb: 3 }}>
      <Icon name="exclamation-triangle" /> This app is for testing purposes only.
    </Heading>

    <Paragraph sx={{ mb: 3 }}>Please change your network to Sepolia.</Paragraph>
  </div>
);

const App = () => {
  const unsupportedNetworkFallback = (chainId: number, supportedNetworkNames?: string[]) => {
    const supportedNetworksString = supportedNetworkNames?.map(nw => capitalize(nw)).join(", ");

    return (
      <div className="flex flex-col items-center justify-center h-screen text-center bg-main text-style-plain">
        <Heading sx={{ mb: 3 }}>
          <Icon name="exclamation-triangle" /> PSY is not yet deployed to{" "}
          {chainId === 1 ? "mainnet" : "this network"}.
        </Heading>
        {supportedNetworksString && <>Please switch to {supportedNetworksString}.</>}
      </div>
    );
  };

  return (
    <>
      <EthersWeb3ReactProvider>
        <ThemeProvider theme={theme}>
          <WalletConnector loader={<AppLoader />}>
            <LiquityProvider
              loader={<AppLoader />}
              unsupportedNetworkFallback={unsupportedNetworkFallback}
              unsupportedMainnetFallback={<UnsupportedMainnetFallback />}
            >
              <TransactionProvider>
                <LiquityFrontend loader={<AppLoader />} />
              </TransactionProvider>
            </LiquityProvider>
          </WalletConnector>
        </ThemeProvider>
      </EthersWeb3ReactProvider>
    </>
  );
};

export default App;
